// src/App.tsx
import React, { useState, useEffect, ReactElement } from 'react';
import MainLayout from './layouts/MainLayout';
import CompetitionManager from './modules/CompetitionManager';
import TeamManager from './modules/TeamManager';
import apiService from './services/api.service';
import { Season } from './utils/types';
import { ORGANIZATIONS } from './utils/constants';

type TabType = 'competition' | 'team';

interface Tab {
  id: TabType;
  name: string;
  icon: ReactElement;
}

const App: React.FC = () => {
  // Get saved org from localStorage or use first org as default
  const savedOrgId = localStorage.getItem('selectedOrgId');
  const defaultOrg = ORGANIZATIONS.find(org => org.id === savedOrgId) || ORGANIZATIONS[0];
  
  const [selectedOrg, setSelectedOrg] = useState(defaultOrg);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('competition');
  const [seasonsLoading, setSeasonsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllSeasons, setShowAllSeasons] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const tabs: Tab[] = [
    { 
      id: 'competition', 
      name: 'Competition Management',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-1 4h1m-1 4h1" />
        </svg>
      )
    },
    { 
      id: 'team', 
      name: 'Team Manager',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }
  ];

  // Fetch seasons when org changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setSeasonsLoading(true);
        setError(null);
        
        // Fetch seasons for selected organization
        const seasonData = await apiService.fetchSeasons(selectedOrg.id);
        
        // Filter seasons with start_date >= 2025-01-01 and clean the data
        const filteredSeasons = seasonData
          .filter(season => {
            const startDate = new Date(season.start_date);
            return startDate >= new Date('2025-01-01');
          })
          .map(season => ({
            ...season,
            name_latin: String(season.name_latin || ''),
            name_local: String(season.name_local || ''),
            competition_name: String(season.competition_name || ''),
            year: Number(season.year) || 2025
          }))
          .sort((a, b) => {
            // Sort by start_date descending (most recent first)
            return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
          });
        
        setSeasons(filteredSeasons);
        
        // Clear selected season when org changes
        setSelectedSeason(null);
        
        // Auto-select first season if available
        if (filteredSeasons.length > 0) {
          setSelectedSeason(filteredSeasons[0]);
        }
      } catch (err: any) {
        console.error('Error fetching seasons:', err);
        setError(err.message || 'Failed to fetch seasons');
      } finally {
        setSeasonsLoading(false);
      }
    };
    
    fetchData();
  }, [selectedOrg]); // Re-fetch when organization changes

  // Handle organization change
  const handleOrgChange = (orgId: string) => {
    const org = ORGANIZATIONS.find(o => o.id === orgId);
    if (org) {
      setSelectedOrg(org);
      // Save to localStorage
      localStorage.setItem('selectedOrgId', org.id);
    }
  };

  // Get quick access seasons (recent/active)
  const getQuickAccessSeasons = () => {
    return seasons
      .filter(s => s.status === 'active' || s.status === 'ongoing' || s.status === 'upcoming')
      .slice(0, 5); // Show max 5 quick access seasons
  };

  // Filter seasons for modal
  const getFilteredSeasons = () => {
    return seasons.filter(season => {
      const matchesSearch = searchQuery === '' || 
        season.name_latin.toLowerCase().includes(searchQuery.toLowerCase()) ||
        season.name_local.toLowerCase().includes(searchQuery.toLowerCase()) ||
        season.competition_name.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || season.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  // Get unique statuses for filter
  const getUniqueStatuses = () => {
    const statuses = new Set(seasons.map(s => s.status || 'Unknown'));
    return Array.from(statuses).sort();
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get season progress
  const getSeasonProgress = (season: Season) => {
    if (season.total_matches && season.total_matches > 0) {
      return Math.round((season.matches_completed / season.total_matches) * 100);
    }
    return 0;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'ongoing':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'upcoming':
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
      case 'finished':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const renderTabContent = () => {
    if (!selectedSeason) {
      return (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <p className="text-gray-500 text-lg">Please select a season to manage permissions</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'competition':
        return (
          <CompetitionManager
            seasonId={selectedSeason.season_id}
            seasonName={String(selectedSeason.name_latin || selectedSeason.name_local)}
            orgId={selectedOrg.id}
          />
        );
      case 'team':
        return (
          <TeamManager
            seasonId={selectedSeason.season_id}
            seasonName={String(selectedSeason.name_latin || selectedSeason.name_local)}
            orgId={selectedOrg.id}
          />
        );
      default:
        return null;
    }
  };

  return (
    <MainLayout title="TMS Permissions Management" subtitle="Manage user access and permissions">
      {error && (
        <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 rounded-xl animate-fade-in">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {String(error)}
          </div>
        </div>
      )}

      {/* Organization Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label htmlFor="org-selector" className="text-sm font-medium text-gray-700">
              Organization:
            </label>
            <select
              id="org-selector"
              value={selectedOrg.id}
              onChange={(e) => handleOrgChange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium"
            >
              {ORGANIZATIONS.map(org => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500">
            Organization ID: <span className="font-mono font-medium">{selectedOrg.id}</span>
          </div>
        </div>
      </div>

      {/* Season Selector Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        {seasonsLoading ? (
          <div className="flex items-center justify-center py-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-gray-500">Loading seasons for {selectedOrg.name}...</span>
          </div>
        ) : seasons.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500">No seasons found for {selectedOrg.name}</p>
            <p className="text-sm text-gray-400 mt-1">Try selecting a different organization</p>
          </div>
        ) : (
          <>
            {/* Selected Season Display */}
            {selectedSeason ? (
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Current Season</div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {selectedSeason.name_latin || selectedSeason.name_local}
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedSeason.competition_name} • {formatDate(selectedSeason.start_date)} - {formatDate(selectedSeason.end_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(selectedSeason.status)}`}>
                      {selectedSeason.status}
                    </span>
                    {getSeasonProgress(selectedSeason) > 0 && (
                      <div className="flex items-center space-x-2">
                        <div className="text-sm text-gray-500">Progress</div>
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${getSeasonProgress(selectedSeason)}%` }}
                          />
                        </div>
                        <div className="text-sm text-gray-600 font-medium">
                          {getSeasonProgress(selectedSeason)}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-2 text-gray-500">
                Select a season to get started
              </div>
            )}

            {/* Quick Access Seasons */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-wrap">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Quick Access:</span>
                  {getQuickAccessSeasons().map((season) => {
                    const isSelected = selectedSeason?.season_id === season.season_id;
                    return (
                      <button
                        key={season.season_id}
                        onClick={() => setSelectedSeason(season)}
                        className={`
                          inline-flex items-center px-3 py-1.5 text-sm rounded-lg transition-all
                          ${isSelected 
                            ? 'bg-blue-600 text-white shadow-sm' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }
                        `}
                      >
                        <span className="truncate max-w-xs">{season.name_short_latin || season.name_latin}</span>
                        {season.status === 'active' && (
                          <span className={`ml-2 w-2 h-2 rounded-full ${isSelected ? 'bg-white' : 'bg-green-500'}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setShowAllSeasons(true)}
                  className="inline-flex items-center px-4 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Browse All Seasons ({seasons.length})
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Tab Navigation */}
      {selectedSeason && (
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-3 px-4 border-b-2 font-medium text-sm rounded-t-lg transition-all duration-200
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-gradient-to-t from-blue-50 to-transparent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                <span className={`mr-2 ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`}>
                  {tab.icon}
                </span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab Content */}
      <div className="animate-fade-in">
        {renderTabContent()}
      </div>

      {/* All Seasons Modal */}
      {showAllSeasons && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Select Competition Season ({getFilteredSeasons().length} results)
                </h3>
                <button
                  onClick={() => setShowAllSeasons(false)}
                  className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex space-x-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search seasons..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Statuses</option>
                  {getUniqueStatuses().map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Seasons Grid */}
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {getFilteredSeasons().length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No seasons found matching your criteria
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {getFilteredSeasons().map((season) => {
                    const isSelected = selectedSeason?.season_id === season.season_id;
                    const progress = getSeasonProgress(season);
                    
                    return (
                      <button
                        key={season.season_id}
                        onClick={() => {
                          setSelectedSeason(season);
                          setShowAllSeasons(false);
                          setSearchQuery('');
                        }}
                        className={`
                          p-4 border rounded-lg text-left transition-all
                          ${isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 line-clamp-1">
                              {season.name_latin || season.name_local}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              {season.competition_name}
                            </div>
                          </div>
                          <span className={`ml-2 inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(season.status)}`}>
                            {season.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 space-y-1">
                          <div>{formatDate(season.start_date)} - {formatDate(season.end_date)}</div>
                          {season.gender && season.age_group && (
                            <div>{season.gender} • {season.age_group}</div>
                          )}
                        </div>
                        {progress > 0 && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-1">
                              <div 
                                className="bg-blue-600 h-1 rounded-full"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default App;